import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { alpha, darken, lighten, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import type { Theme } from "@mui/material/styles";
import type { ECElementEvent, ECharts } from "echarts";
import { EChartsRenderer } from "@/shared/charts/components/echarts-renderer";
import { findEditablePointAtPixel } from "@/shared/charts/components/geo-scatter-chart/editable-point-hit-test";
import { isPixelInsidePolygon } from "@/shared/charts/components/geo-scatter-chart/polygon-hit-test";
import { focusGeoOnBoundaries } from "@/shared/charts/components/geo-scatter-chart/focus-boundaries";
import {
  createBoundariesSeries,
  createGeoScatterChartOption,
  DEFAULT_ZOOM_LIMIT,
} from "@/shared/charts/components/geo-scatter-chart/option";
import { createEChartsTheme } from "@/shared/charts/theme/create-echarts-theme";
import type {
  GeoDirectionalLine,
  GeoHeatmapLayer,
  GeoLineSegment,
  GeoScatterBoundary,
  GeoScatterChartConfig,
  GeoScatterMarker,
  GeoScatterPoint,
  SecondaryCoordinate,
} from "@/shared/charts/components/geo-scatter-chart/types";

/**
 * Dev-only cursor coordinates readout (Ctrl+hover), loaded only when
 * `import.meta.env.DEV` is statically `true`; Vite/esbuild eliminate the
 * unreachable branch (and therefore the dynamic `import()` target) entirely
 * from production builds.
 */
const DevCursorCoordinatesOverlay = import.meta.env.DEV
  ? lazy(() =>
      import("@/shared/charts/components/geo-scatter-chart/dev-cursor-coordinates-overlay").then(
        (module) => ({ default: module.DevCursorCoordinatesOverlay })
      )
    )
  : null;

interface GeoScatterChartProps {
  markers: GeoScatterMarker[];
  /** Non-selectable live-position dots. */
  points?: GeoScatterPoint[];
  /** Non-interactive map-aligned line segments rendered below dots and markers. */
  lineSegments?: GeoLineSegment[];
  /** Direction-aware line annotations rendered in the geo coordinate system. */
  directionalLines?: GeoDirectionalLine[];
  /** Soft map-aligned raster rendered below dots and markers. */
  heatmapLayer?: GeoHeatmapLayer | null;
  /** Highlighted polygonal regions (e.g. observation areas), always rendered below the heatmap and every dot/marker. */
  boundaries?: GeoScatterBoundary[];
  config: GeoScatterChartConfig;
  /** Invoked with a marker's `id` when it's clicked. */
  onSelectMarker?: (id: string) => void;
  /** Invoked with a geo data-space point when the map canvas is clicked. */
  onMapClick?: (point: [number, number]) => void;
  /** Existing points that can be repositioned by dragging them on the map. */
  editablePoints?: GeoScatterPoint[];
  /** Invoked continuously while an editable point is dragged. */
  onMovePoint?: (id: string, point: [number, number]) => void;
  /** Whether the polygon formed by `editablePoints` can be moved as a whole from its interior. */
  canMoveEditableShape?: boolean;
  /** Invoked with an editable point's id while hovered (not dragging), or null once the cursor leaves it. */
  onHoverPoint?: (id: string | null) => void;
  /** Invoked with a geo data-space point when the map canvas is double-clicked. */
  onMapDoubleClick?: (point: [number, number]) => void;
  /** Clickable "+" markers placed at, e.g., an editable boundary's edge midpoints, for inserting a new vertex there. */
  insertionPoints?: GeoScatterPoint[];
  /** Invoked with an insertion point's id when it's clicked. */
  onInsertPoint?: (id: string) => void;
  /** Boundaries used for auto-focus; defaults to all rendered `boundaries`. */
  focusBoundaries?: GeoScatterBoundary[];
  /**
   * Bump this (e.g. a counter) to force the geo layer to re-focus on
   * `boundaries` even after the user has manually panned/zoomed away from it
   * (see the auto-focus behavior below).
   */
  focusRequestId?: number;
  /**
   * Notified whenever the geo layer becomes focused on `boundaries` (`true`)
   * or the user manually pans/zooms away from that focus (`false`).
   */
  onFocusedChange?: (focused: boolean) => void;
  /** Optional callback to reset/refocus the view on boundaries. */
  onResetFocus?: () => void;
  height?: number | string;
  testId?: string;
  /**
   * Dev-only, business-agnostic hook (see `DevCursorCoordinatesOverlay`): given
   * the geo data-space `[x, y]` point under the cursor, returns an additional
   * coordinate to show and Ctrl+click-copy alongside the pixel coordinate
   * (e.g. a world/UTM position), or `null` to show/copy only the pixel
   * coordinate. Has no effect in production builds.
   */
  resolveSecondaryCoordinate?: (point: [number, number]) => SecondaryCoordinate | null;
}

/**
 * Renders a roam-able (zoom/pan) ECharts geo map with a scatter overlay of
 * selectable, styled markers and an optional heatmap below them. The chart is
 * updated in merge mode so the current zoom and pan survive live data updates.
 *
 * Whenever `boundaries` first appears or changes (e.g. a different area
 * selection), the geo layer smoothly zooms/pans to fit them - but only until
 * the user manually pans or zooms the map themselves, at which point that
 * auto-focus stops so it doesn't fight their input. Bumping `focusRequestId`
 * (e.g. from a "Focus Selected Area(s)" button) re-focuses on demand and
 * re-enables auto-focus for later boundary changes.
 */
export function GeoScatterChart({
  markers,
  points = [],
  lineSegments = [],
  directionalLines = [],
  heatmapLayer = null,
  boundaries = [],
  config,
  onSelectMarker,
  onMapClick,
  editablePoints = [],
  onMovePoint,
  canMoveEditableShape = false,
  onHoverPoint,
  onMapDoubleClick,
  insertionPoints = [],
  onInsertPoint,
  focusBoundaries,
  focusRequestId,
  onFocusedChange,
  onResetFocus,
  height = "100%",
  testId,
  resolveSecondaryCoordinate,
}: GeoScatterChartProps) {
  const muiTheme = useTheme();
  const chartTokens = useMemo(() => createEChartsTheme(muiTheme), [muiTheme]);
  const zoomLimit = config.zoomLimit ?? DEFAULT_ZOOM_LIMIT;
  const [zoom, setZoom] = useState(zoomLimit.min);
  const option = useMemo(
    () =>
      createGeoScatterChartOption(
        markers,
        points,
        heatmapLayer,
        boundaries,
        config,
        chartTokens,
        lineSegments,
        insertionPoints,
        directionalLines,
        zoom
      ),
    [
      markers,
      points,
      lineSegments,
      directionalLines,
      heatmapLayer,
      boundaries,
      config,
      chartTokens,
      insertionPoints,
      zoom,
    ]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);
  // Assumed focused by default so the auto-focus effect runs once on mount;
  // see `updateFocused` for how this stays in sync with `isFocused` for reads
  // that shouldn't themselves retrigger the focus effect.
  const [isFocused, setIsFocused] = useState(true);
  const isFocusedRef = useRef(isFocused);
  const lastFocusRequestIdRef = useRef(focusRequestId);
  const cancelFocusRef = useRef<(() => void) | null>(null);
  const editablePointsRef = useRef(editablePoints);
  const insertionPointsRef = useRef(insertionPoints);
  const onMovePointRef = useRef(onMovePoint);
  const onHoverPointRef = useRef(onHoverPoint);
  const onMapClickRef = useRef(onMapClick);
  const canMovePoints = onMovePoint != null;
  const draggingPointIdRef = useRef<string | null>(null);
  const hoveredPointIdRef = useRef<string | null>(null);
  const suppressNextMapClickRef = useRef(false);
  // Snapshot of every editable point's id/position plus the geo-space point
  // under the cursor when a whole-shape drag (mousedown inside the polygon,
  // away from any vertex) begins; used to translate every vertex by the same
  // delta as the cursor moves. Null whenever no shape drag is in progress.
  const shapeDragRef = useRef<{
    startGeoPoint: [number, number];
    snapshot: { id: string; position: [number, number] }[];
  } | null>(null);
  editablePointsRef.current = editablePoints;
  insertionPointsRef.current = insertionPoints;
  onMovePointRef.current = onMovePoint;
  onHoverPointRef.current = onHoverPoint;
  onMapClickRef.current = onMapClick;

  function updateFocused(next: boolean) {
    isFocusedRef.current = next;
    setIsFocused(next);
  }

  useEffect(() => {
    onFocusedChange?.(isFocused);
  }, [isFocused, onFocusedChange]);

  // A user-driven pan/zoom (mouse drag/wheel) dispatches ECharts' built-in
  // `geoRoam` action/event; our own auto-focus animation drives `center`/`zoom`
  // via `setOption` instead, so it never triggers this. Once the user takes
  // over, stop auto-focusing so it doesn't fight their input.
  useEffect(() => {
    if (!chartInstance) return;

    function handleManualRoam() {
      updateFocused(false);
      cancelFocusRef.current?.();
    }

    chartInstance.on("georoam", handleManualRoam);
    return () => {
      chartInstance.off("georoam", handleManualRoam);
    };
  }, [chartInstance]);

  useEffect(() => {
    if (!chartInstance || !canMovePoints) return;
    const renderer = chartInstance.getZr();
    const chartRoot = chartInstance.getDom();
    const cursorTargets = [chartRoot, ...chartRoot.querySelectorAll("canvas")];
    const setInteractionCursor = (cursor: string) => {
      renderer.setCursorStyle(cursor);
      // ECharts can apply its own inline cursor while processing the same mouse
      // event. Pin the interaction cursor on both the chart root and canvas so
      // vertex/shape drag state wins consistently in Firefox and Chromium.
      cursorTargets.forEach((target) => target.style.setProperty("cursor", cursor, "important"));
    };

    const toGeoPoint = (pixel: [number, number]): [number, number] | null => {
      try {
        if (!chartInstance.containPixel({ geoIndex: 0 }, pixel)) return null;
        return (
          (chartInstance.convertFromPixel({ geoIndex: 0 }, pixel) as
            [number, number] | undefined) ?? null
        );
      } catch {
        return null;
      }
    };
    const toPixel = (point: [number, number]): [number, number] | null => {
      try {
        return (
          (chartInstance.convertToPixel({ geoIndex: 0 }, point) as [number, number] | undefined) ??
          null
        );
      } catch {
        return null;
      }
    };
    // Idle cursor: `crosshair` only signals "clicking here adds a point",
    // which is only true while `onMapClick` is wired up (create mode); edit
    // mode has no map-click behavior, so it falls back to the default arrow.
    const idleCursor = () => (onMapClickRef.current != null ? "crosshair" : "default");
    const finishDrag = () => {
      if (draggingPointIdRef.current == null && shapeDragRef.current == null) return;
      draggingPointIdRef.current = null;
      shapeDragRef.current = null;
      chartInstance.setOption({ geo: { roam: true } });
      setInteractionCursor(idleCursor());
    };
    const setHoveredPoint = (id: string | null) => {
      if (hoveredPointIdRef.current === id) return;
      hoveredPointIdRef.current = id;
      onHoverPointRef.current?.(id);
    };
    const handleMouseDown = (event: { offsetX: number; offsetY: number; stop?: () => void }) => {
      const pixel: [number, number] = [event.offsetX, event.offsetY];
      const point = findEditablePointAtPixel(editablePointsRef.current, pixel, toPixel, 14);
      if (point) {
        draggingPointIdRef.current = point.id;
        suppressNextMapClickRef.current = true;
        chartInstance.setOption({ geo: { roam: false } });
        setInteractionCursor("grabbing");
        event.stop?.();
        return;
      }

      // An insertion "+" marker sits exactly on an edge, which can also read as
      // "inside" the polygon below; leave it alone so its own click handler
      // (wired up separately, on the chart's `click` event) still fires.
      if (findEditablePointAtPixel(insertionPointsRef.current, pixel, toPixel, 14)) return;

      if (
        !canMoveEditableShape ||
        !isPixelInsidePolygon(pixel, editablePointsRef.current, toPixel)
      ) {
        return;
      }
      const startGeoPoint = toGeoPoint(pixel);
      if (!startGeoPoint) return;

      shapeDragRef.current = {
        startGeoPoint,
        snapshot: editablePointsRef.current.map((editablePoint) => ({
          id: editablePoint.id,
          position: editablePoint.position,
        })),
      };
      suppressNextMapClickRef.current = true;
      chartInstance.setOption({ geo: { roam: false } });
      setInteractionCursor("move");
      event.stop?.();
    };
    const handleMouseMove = (event: { offsetX: number; offsetY: number }) => {
      const pixel: [number, number] = [event.offsetX, event.offsetY];
      const pointId = draggingPointIdRef.current;
      if (pointId) {
        setInteractionCursor("grabbing");
        const point = toGeoPoint(pixel);
        if (point) onMovePointRef.current?.(pointId, point);
        return;
      }

      const shapeDrag = shapeDragRef.current;
      if (shapeDrag) {
        setInteractionCursor("move");
        const currentGeoPoint = toGeoPoint(pixel);
        if (currentGeoPoint) {
          const deltaX = currentGeoPoint[0] - shapeDrag.startGeoPoint[0];
          const deltaY = currentGeoPoint[1] - shapeDrag.startGeoPoint[1];
          for (const { id, position } of shapeDrag.snapshot) {
            onMovePointRef.current?.(id, [position[0] + deltaX, position[1] + deltaY]);
          }
        }
        return;
      }

      const hovered = findEditablePointAtPixel(editablePointsRef.current, pixel, toPixel, 14);
      if (hovered) {
        setInteractionCursor("pointer");
        setHoveredPoint(hovered.id);
        return;
      }
      setHoveredPoint(null);

      if (findEditablePointAtPixel(insertionPointsRef.current, pixel, toPixel, 14)) {
        setInteractionCursor("pointer");
        return;
      }

      setInteractionCursor(
        canMoveEditableShape && isPixelInsidePolygon(pixel, editablePointsRef.current, toPixel)
          ? "move"
          : idleCursor()
      );
    };
    const handleMouseLeave = () => {
      if (draggingPointIdRef.current != null || shapeDragRef.current != null) return;
      setInteractionCursor(idleCursor());
      setHoveredPoint(null);
    };

    renderer.on("mousedown", handleMouseDown);
    renderer.on("mousemove", handleMouseMove);
    renderer.on("mouseup", finishDrag);
    renderer.on("globalout", finishDrag);
    renderer.on("globalout", handleMouseLeave);
    return () => {
      renderer.off("mousedown", handleMouseDown);
      renderer.off("mousemove", handleMouseMove);
      renderer.off("mouseup", finishDrag);
      renderer.off("globalout", finishDrag);
      renderer.off("globalout", handleMouseLeave);
      cursorTargets.forEach((target) => target.style.removeProperty("cursor"));
    };
  }, [chartInstance, canMovePoints, canMoveEditableShape]);

  useEffect(() => {
    if (!chartInstance || onMapClick == null) return;
    const renderer = chartInstance.getZr();

    const handleMapClick = (event: { offsetX: number; offsetY: number }) => {
      if (suppressNextMapClickRef.current) {
        suppressNextMapClickRef.current = false;
        return;
      }
      const pixel: [number, number] = [event.offsetX, event.offsetY];
      if (!chartInstance.containPixel({ geoIndex: 0 }, pixel)) return;

      const point = chartInstance.convertFromPixel({ geoIndex: 0 }, pixel) as
        [number, number] | undefined;
      if (point) onMapClick(point);
    };

    renderer.on("click", handleMapClick);
    return () => renderer.off("click", handleMapClick);
  }, [chartInstance, onMapClick]);

  useEffect(() => {
    if (!chartInstance || onMapDoubleClick == null) return;
    const renderer = chartInstance.getZr();

    const handleMapDoubleClick = (event: { offsetX: number; offsetY: number }) => {
      const pixel: [number, number] = [event.offsetX, event.offsetY];
      if (!chartInstance.containPixel({ geoIndex: 0 }, pixel)) return;

      const point = chartInstance.convertFromPixel({ geoIndex: 0 }, pixel) as
        [number, number] | undefined;
      if (point) onMapDoubleClick(point);
    };

    renderer.on("dblclick", handleMapDoubleClick);
    return () => renderer.off("dblclick", handleMapDoubleClick);
  }, [chartInstance, onMapDoubleClick]);

  const boundariesToFocus = focusBoundaries ?? boundaries;

  // Keeps the zoom-in/out button's enabled state in sync with the geo layer's
  // actual zoom, whether it changed from a user-driven wheel/drag roam, our
  // own zoom button (both go through the `geoRoam` action), or the auto-focus
  // animation (which drives `zoom` directly via `setOption`, see below).
  useEffect(() => {
    if (!chartInstance) return;

    const syncZoomFromOption = () => {
      const geoOption = (
        chartInstance.getOption().geo as Array<{ zoom?: number }> | undefined
      )?.[0];
      if (geoOption?.zoom != null) setZoom(geoOption.zoom);
    };

    syncZoomFromOption();
    chartInstance.on("finished", syncZoomFromOption);
    return () => {
      chartInstance.off("finished", syncZoomFromOption);
    };
  }, [chartInstance]);

  /** Matches ECharts' own default large-tick mouse-wheel zoom factor (see `RoamController`'s `_mousewheelHandler`), so each button click feels like one wheel notch. */
  const ZOOM_STEP_FACTOR = 1.4;
  const canZoomIn = zoom < zoomLimit.max;
  const canZoomOut = zoom > zoomLimit.min;

  function zoomBy(factor: number) {
    if (!chartInstance) return;

    // Dispatching the same `geoRoam` action ECharts' own roam controller uses
    // for mouse wheel/pinch zoom, so button clicks behave identically
    // (including firing `georoam`, which stops auto-focus, and clamping to
    // `zoomLimit`).
    chartInstance.dispatchAction({
      type: "geoRoam",
      componentType: "geo",
      geoIndex: 0,
      zoom: factor,
      originX: chartInstance.getWidth() / 2,
      originY: chartInstance.getHeight() / 2,
    });
  }

  const animatedBoundaries = boundaries.filter((boundary) => boundary.style?.animated === true);
  const pulsingBoundaries = boundaries.filter((boundary) => boundary.style?.pulse === true);
  const boundariesRef = useRef(boundaries);
  boundariesRef.current = boundaries;
  const boundaryDefaultStyleRef = useRef(config.boundaryStyle);
  boundaryDefaultStyleRef.current = config.boundaryStyle;

  // Drives a "marching ants" effect for boundaries flagged `animated` (e.g. a
  // selected AOI) by continuously shifting their dashed outline's offset,
  // without touching stroke/fill colors. Only the lightweight "boundaries"
  // series is patched via `setOption` each frame, so this never re-renders
  // React or recomputes the rest of the chart option.
  useEffect(() => {
    if (!chartInstance || (animatedBoundaries.length === 0 && pulsingBoundaries.length === 0)) {
      return;
    }

    let animationFrame: number;
    let dashOffset = 0;
    const startTime = performance.now();
    const pulsePeriodMs = 1800;
    const pulseMinOpacity = 0.25;
    const pulseMaxOpacity = 0.9;

    const tick = () => {
      dashOffset += 0.6;
      const phase = ((performance.now() - startTime) % pulsePeriodMs) / pulsePeriodMs;
      const pulseOpacity =
        pulseMinOpacity +
        (pulseMaxOpacity - pulseMinOpacity) * (0.5 + 0.5 * Math.sin(phase * Math.PI * 2));
      chartInstance.setOption({
        series: [
          createBoundariesSeries(
            boundariesRef.current,
            boundaryDefaultStyleRef.current ?? {},
            dashOffset,
            pulseOpacity
          ),
        ],
      });
      animationFrame = requestAnimationFrame(tick);
    };
    animationFrame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animationFrame);
  }, [chartInstance, animatedBoundaries.length, pulsingBoundaries.length]);

  useEffect(() => {
    if (!chartInstance || boundariesToFocus.length === 0) return;
    const container = containerRef.current;
    if (!container) return;

    const isExplicitFocusRequest = focusRequestId !== lastFocusRequestIdRef.current;
    lastFocusRequestIdRef.current = focusRequestId;
    if (!isExplicitFocusRequest && !isFocusedRef.current) return;
    updateFocused(true);

    const cancel = focusGeoOnBoundaries(
      chartInstance,
      container,
      boundariesToFocus,
      config.zoomLimit ?? DEFAULT_ZOOM_LIMIT
    );
    cancelFocusRef.current = cancel;
    return () => {
      cancel();
      cancelFocusRef.current = null;
    };
  }, [chartInstance, boundariesToFocus, config.zoomLimit, focusRequestId]);

  function handleChartClick(params: ECElementEvent) {
    if (params.componentType !== "series") return;
    const data = params.data as { id?: string } | undefined;
    if (params.seriesId === "markers") {
      if (onSelectMarker != null && data?.id != null) onSelectMarker(data.id);
      return;
    }
    if (params.seriesId === "aoi-insertions") {
      if (onInsertPoint != null && data?.id != null) onInsertPoint(data.id);
    }
  }

  /**
   * Explicit, fully opaque background colors for every interaction state:
   * `action.hover`/`action.disabledBackground` are meant to be translucent
   * overlays composited over a surface, so using them directly as a solid
   * `backgroundColor` here (over the map canvas, not a surface) made the
   * buttons look washed-out/transparent on hover and nearly invisible when
   * disabled.
   */
  const zoomButtonSx = {
    backgroundColor: "background.paper",
    boxShadow: 2,
    width: 56,
    height: 56,
    "&:hover": {
      backgroundColor: (theme: Theme) =>
        theme.palette.mode === "dark"
          ? lighten(theme.palette.background.paper, 0.15)
          : darken(theme.palette.background.paper, 0.08),
    },
    "&.Mui-disabled": {
      backgroundColor: (theme: Theme) => alpha(theme.palette.background.paper, 0.7),
      color: "action.disabled",
    },
  };

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: "100%", height }}>
      <EChartsRenderer
        option={option}
        ariaLabel={config.ariaLabel}
        height={height}
        testId={testId}
        notMerge={false}
        replaceMerge="series"
        onEvents={{ click: handleChartClick }}
        onChartReady={setChartInstance}
      />
      {DevCursorCoordinatesOverlay && (
        <Suspense fallback={null}>
          <DevCursorCoordinatesOverlay
            containerRef={containerRef}
            chartInstance={chartInstance}
            resolveSecondaryCoordinate={resolveSecondaryCoordinate}
          />
        </Suspense>
      )}
      <Box
        sx={{
          position: "absolute",
          right: 12,
          bottom: 12,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Tooltip title="Zoom in" placement="left">
          <span>
            <IconButton
              size="medium"
              aria-label="Zoom in"
              disabled={!canZoomIn}
              onClick={() => zoomBy(ZOOM_STEP_FACTOR)}
              sx={zoomButtonSx}
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Zoom out" placement="left">
          <span>
            <IconButton
              size="medium"
              aria-label="Zoom out"
              disabled={!canZoomOut}
              onClick={() => zoomBy(1 / ZOOM_STEP_FACTOR)}
              sx={zoomButtonSx}
            >
              <RemoveIcon />
            </IconButton>
          </span>
        </Tooltip>
        {onResetFocus && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Tooltip
              title={
                isFocused
                  ? "Map is already focused on the selected area"
                  : "Reset view to selected area"
              }
              placement="left"
            >
              <span>
                <IconButton
                  size="medium"
                  aria-label="Reset view to area"
                  disabled={isFocused}
                  onClick={onResetFocus}
                  sx={zoomButtonSx}
                >
                  <RestartAltIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}
